import {
    type ComputedRef,
    type Ref,
    ref,
    toRef,
    watch,
    type WritableComputedRef,
} from 'vue'
import { useRoute } from 'vue-router'
import { defineStore, storeToRefs } from 'pinia'

import type {
    QuestionnaireAnswerValueInput,
    QuestionnaireFragment,
    QuestionnaireQuestionChoiceInput,
    QuestionnaireQuestionChoiceOptionBehaviourFragment,
    QuestionnaireQuestionFragment,
    QuestionnaireStepFragment,
    QuestionnaireType,
} from '@/graphql/api'
import {
    type QuestionnaireStoreAnswers,
    useQuestionnaireStore,
} from './useQuestionnaireStore'
import type { NonNullableKeys } from '@/types/utils'
import { useSegment } from '@/third-party/segment.ts'

type TrackParams = Record<string, string | undefined>
type NavigationEventApp = 'Next' | string | 'app_back'
type NavigationEventBrowser = 'browser_forward' | 'browser_back'
type NavigationEvent = NavigationEventApp | NavigationEventBrowser
type CtaEvent = Extract<'Next' | string | 'browser_forward', NavigationEvent>

const formatStepProgress = (current: number, total: number) =>
    `${current}-${total}`

const defaultSubmitBtnLabel = "Let's get started"

const formatQuestoionTitle = (
    questionnaire: QuestionnaireFragment,
    question: QuestionnaireQuestionFragment | null | undefined
) =>
    question?.title ? `${questionnaire.title} - ${question.title}` : undefined

const isCtaEvent = (
    event: NavigationEvent,
    submitBtnLabel: string
): event is CtaEvent =>
    event === 'Next' || event === submitBtnLabel || event === 'browser_forward'

const getStepIndexNumber = (
    currentStepIndex: WritableComputedRef<number | 'complete'>,
    stepCount: ComputedRef<number>,
    isCompletedStepEvent = false
) => {
    if (isCompletedStepEvent) {
        return currentStepIndex.value === 'complete'
            ? stepCount.value - 1
            : currentStepIndex.value
    }

    return currentStepIndex.value === 'complete'
        ? stepCount.value
        : currentStepIndex.value + 1
}

const getQuestionOptionBehaviour = (
    stepContent: QuestionnaireStepFragment['content'] | null | undefined,
    answers: QuestionnaireStoreAnswers,
    questionnaireId: string
): QuestionnaireQuestionChoiceOptionBehaviourFragment | null => {
    const question =
        stepContent?.[0]?.__typename === 'QuestionnaireStepContentQuestions'
            ? stepContent?.[0]?.questions[0]
            : null

    if (!question) {
        return null
    }

    const options =
        question.__typename === 'QuestionnaireQuestionChoice'
            ? question?.options
            : null

    if (!options) {
        return null
    }

    const answer = answers[questionnaireId]?.[question.id]

    if (!answer) {
        return null
    }

    const answerId = answer.answer.answer.singleChoice?.referenceId

    const option = options.find((option) => option.id === answerId)

    if (!option) {
        return null
    }

    return option.behaviour
}

const getAnswerChoiceTitle = (choice: QuestionnaireQuestionChoiceInput) =>
    choice.layout.iconCard?.title ||
    choice.layout.image?.image ||
    choice.layout.text?.title ||
    choice.layout.imageCard?.title

const getAnswerTitle = (answer: QuestionnaireAnswerValueInput) =>
    answer.multiChoice?.map((a) => getAnswerChoiceTitle(a)).join(', ') ||
    (answer.singleChoice && getAnswerChoiceTitle(answer.singleChoice)) ||
    answer.text?.text ||
    String(answer.number?.number)

const getQuestion = (stepContent: QuestionnaireStepFragment['content']) => {
    const content = stepContent[0]

    if (!content) {
        return undefined
    }

    if (content.__typename !== 'QuestionnaireStepContentQuestions') {
        return undefined
    }

    return content.questions[0]
}

const getQuestionAnswer = (
    stepContent: QuestionnaireStepFragment['content'],
    answers: QuestionnaireStoreAnswers,
    questionnaireId: string
) => {
    const questionId = getQuestion(stepContent)?.id

    if (!questionId) {
        return undefined
    }

    const answer = answers[questionnaireId]?.[questionId]?.answer.answer

    if (!answer) {
        return undefined
    }

    return getAnswerTitle(answer)
}

const useQuestionnaireTrackingStore = defineStore(
    'questionnaireTracking',
    () => {
        const trackedData: Ref<
            Record<
                QuestionnaireType | string,
                NonNullableKeys<QuestionnaireQuestionChoiceOptionBehaviourFragment>
            >
        > = ref({})

        const recordBehaviour = (
            questionnaire: QuestionnaireType,
            behaviour: QuestionnaireQuestionChoiceOptionBehaviourFragment
        ) => {
            const nonNullableBehaviour = Object.fromEntries(
                Object.entries(behaviour).filter(([, value]) => value !== null)
            ) as NonNullableKeys<QuestionnaireQuestionChoiceOptionBehaviourFragment>

            if (trackedData.value[questionnaire]) {
                trackedData.value[questionnaire] = {
                    ...trackedData.value[questionnaire],
                    ...nonNullableBehaviour,
                }
            } else {
                trackedData.value[questionnaire] = nonNullableBehaviour
            }
        }

        const readBehaviour = (
            questionnaire: QuestionnaireType
        ): Partial<
            NonNullableKeys<QuestionnaireQuestionChoiceOptionBehaviourFragment>
        > | null => {
            return trackedData.value[questionnaire] || null
        }

        const resetTrackedData = () => {
            trackedData.value = {} as Record<
                QuestionnaireType,
                NonNullableKeys<QuestionnaireQuestionChoiceOptionBehaviourFragment>
            >
        }

        return {
            recordBehaviour,
            readBehaviour,
            resetTrackedData,
        }
    }
)

function useCta(
    currentStepIndex: WritableComputedRef<number | 'complete'>,
    submitBtnLabel: Readonly<Ref<string>>
) {
    const cta: Ref<CtaEvent | undefined> = ref()

    const navigationHookBrowser = (event: NavigationEventBrowser) => {
        if (!isCtaEvent(event, submitBtnLabel.value)) {
            return
        }

        // if the value is already set, it means that
        // it was set by the app navigation hook
        if (cta.value) {
            return
        }

        cta.value = event
    }

    watch(currentStepIndex, (newStepIndex, oldStepIndex) => {
        if (newStepIndex === 'complete') {
            navigationHookBrowser('browser_forward')

            return
        }

        if (oldStepIndex === 'complete') {
            navigationHookBrowser('browser_back')

            return
        }

        if (newStepIndex > oldStepIndex) {
            navigationHookBrowser('browser_forward')
        } else {
            navigationHookBrowser('browser_back')
        }
    })

    const navigationHookApp = (event: NavigationEventApp) => {
        if (!isCtaEvent(event, submitBtnLabel.value)) {
            return
        }

        cta.value = event
    }

    const ctaReset = () => {
        cta.value = undefined
    }

    return {
        cta,
        navigationHookApp,
        ctaReset,
    }
}

export interface QuestionnaireTracking {
    navigationHookApp: (event: NavigationEventApp) => void
    stepCompletedLast: () => void
    questionnaireExit: () => void
}

export function useQuestionnaireTracking(
    questionnaireRef: Readonly<Ref<QuestionnaireFragment | null | undefined>>,
    currentStep: ComputedRef<
        QuestionnaireStepFragment['content'] | null | undefined
    >,
    stepCount: ComputedRef<number>,
    currentStepIndex: WritableComputedRef<number | 'complete'>
): QuestionnaireTracking {
    const { track } = useSegment()

    const { cta, navigationHookApp, ctaReset } = useCta(
        currentStepIndex,
        toRef(
            () =>
                questionnaireRef.value?.signUpStep.submitButtonLabel ??
                defaultSubmitBtnLabel
        )
    )

    const t = (
        event: string,
        params: TrackParams,
        questionnaireType: QuestionnaireType
    ) => track(`${questionnaireType.toLowerCase()}_${event}`, params)

    const trackEventViewed = (
        params: TrackParams,
        questionnaireType: QuestionnaireType
    ) => t('viewed', params, questionnaireType)

    const trackEventStepCompleted = (
        params: TrackParams,
        questionnaireType: QuestionnaireType
    ) => t('step_completed', params, questionnaireType)

    const trackEventQuestionnaireExit = (
        params: TrackParams,
        questionnaireType: QuestionnaireType
    ) => t('step_exit', params, questionnaireType)

    const questionnaireStartTimestamp = Date.now()
    // handle KeepAlive behaviour
    // onMounted(() => {
    //     questionnaireStartTimestamp = Date.now()
    // })

    let stepStartTimestamp = Date.now()

    const stepViewed = () => {
        const questionnaire = questionnaireRef.value

        if (!questionnaire) {
            return
        }

        stepStartTimestamp = Date.now()

        if (!currentStep.value) {
            if (currentStepIndex.value === 'complete') {
                const params: TrackParams = {
                    name: questionnaire.title,
                    step: formatStepProgress(stepCount.value, stepCount.value),
                }

                trackEventViewed(params, questionnaire.type)
            }

            return
        }

        const question = getQuestion(currentStep.value)

        const params: TrackParams = {
            name: questionnaire.title,
            step: formatStepProgress(
                getStepIndexNumber(currentStepIndex, stepCount),
                stepCount.value
            ),
            question: formatQuestoionTitle(questionnaire, question),
        }

        if (currentStepIndex.value === 0) {
            params['status'] = 'questionnaire_started'
        }

        trackEventViewed(params, questionnaire.type)
    }

    const { readBehaviour, recordBehaviour, resetTrackedData } =
        useQuestionnaireTrackingStore()
    const { answers } = storeToRefs(useQuestionnaireStore())

    const stepCompleted = (
        step: QuestionnaireStepFragment['content'] | null | undefined,
        questionnaireId: string
    ) => {
        const questionnaire = questionnaireRef.value

        if (!questionnaire) {
            return
        }

        if (!step) {
            return
        }

        // Proceed only on forward navigation
        if (!cta.value) {
            return
        }

        const question = getQuestion(step)
        const optionSelected = getQuestionAnswer(
            step,
            answers.value,
            questionnaireId
        )

        const stepBehaviour = getQuestionOptionBehaviour(
            step,
            answers.value,
            questionnaireId
        )

        if (stepBehaviour) {
            recordBehaviour(questionnaire.type, stepBehaviour)
        }

        const questionnaireBehaviour =
            readBehaviour(questionnaire.type) ||
            ({} as NonNullableKeys<QuestionnaireQuestionChoiceOptionBehaviourFragment>)

        delete questionnaireBehaviour['__typename']

        const params: TrackParams = {
            name: questionnaire.title,
            step: formatStepProgress(
                getStepIndexNumber(currentStepIndex, stepCount, true),
                stepCount.value
            ),
            question: formatQuestoionTitle(questionnaire, question),
            option_selected: optionSelected,
            ...questionnaireBehaviour,
            time_to_complete_step: String(Date.now() - stepStartTimestamp),
            cta_clicked: cta.value,
        }

        trackEventStepCompleted(params, questionnaire.type)
    }

    const stepCompletedLast = () => {
        const questionnaire = questionnaireRef.value

        if (!questionnaire) {
            return
        }

        const cta_clicked: CtaEvent =
            questionnaireRef.value?.signUpStep.submitButtonLabel ??
            defaultSubmitBtnLabel

        const params: TrackParams = {
            name: questionnaire.title,
            step: formatStepProgress(stepCount.value, stepCount.value),
            time_to_complete_step: String(Date.now() - stepStartTimestamp),
            time_to_complete_questionnaire: String(
                Date.now() - questionnaireStartTimestamp
            ),
            status: 'questionnaire_completed',
            cta_clicked,
        }

        trackEventStepCompleted(params, questionnaire.type)

        resetTrackedData()
    }

    const questionnaireExit = () => {
        const questionnaire = questionnaireRef.value

        if (!questionnaire) {
            return
        }

        const params: TrackParams = {
            name: questionnaire?.title,
            step: formatStepProgress(
                getStepIndexNumber(currentStepIndex, stepCount),
                stepCount.value
            ),
        }

        trackEventQuestionnaireExit(params, questionnaire.type)

        resetTrackedData()
    }

    const route = useRoute()

    watch(
        currentStep,
        (_newStep, oldStep) => {
            // ebalosie
            if (route.name !== 'questionnaire') {
                return
            }

            if (oldStep) {
                stepCompleted(oldStep, route.params['id'] as string)

                ctaReset()
            }

            stepViewed()
        },
        { immediate: true }
    )

    return {
        navigationHookApp,
        stepCompletedLast,
        questionnaireExit,
    }
}
